import { useCallback, useContext } from 'react';

import { logger } from '@packages/shared';
import { TrackingContext } from './TrackingContext';
import type { GenericTrackingEvent } from './types';

const dispatchEvent = <T extends GenericTrackingEvent>(
  event: T,
  dataLayerName = 'dataLayer',
): void => {
  if (typeof window !== 'undefined' && (window as { [key: string]: any })[dataLayerName]) {
    (window as { [key: string]: any })[dataLayerName].push(event);
  } else if (typeof window !== 'undefined') {
    logger.warn('[GTM] No datalayer!');
  }
};

export const useTracking = (): (<T extends GenericTrackingEvent>(event: T) => void) => {
  const context = useContext(TrackingContext);
  const dataLayerName = context?.dataLayerName;

  if (context === null || typeof dataLayerName === 'undefined') {
    logger.error('[useTracking]: hook use outside TrackingProvider component.');
  } else if (typeof window !== 'undefined' && !(window as { [key: string]: any })[dataLayerName]) {
    (window as { [key: string]: any })[dataLayerName] = [];
  }

  return useCallback(
    <T extends GenericTrackingEvent>(event: T) =>
      !dataLayerName ? () => {} : dispatchEvent<T>(event, dataLayerName),
    [dataLayerName],
  );
};

export const useIsBot = (): boolean => {
  const context = useContext(TrackingContext);

  if (context === null) {
    logger.error('[useTracking]: hook use outside TrackingProvider component.');
    return false;
  }

  const { isBot } = context;

  return isBot;
};
